/**
 * features/burs/BursHub.tsx
 * ------------------------------------------------------------------
 * BURS — statutory returns hub. One screen, one hero, a sub-tab per
 * form in filing order, plus the employer details the forms print.
 *
 *   1. ITW-7   monthly PAYE return
 *   2. ITW-8   employee annual tax certificates
 *   3. ITW-10  annual PAYE reconciliation
 *   4. Employer — name / TIN / address printed on every return
 *
 * All figures derive from the immutable generated payslips, so a
 * month/year must have a completed Pay Run before its return fills.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Landmark, FileText, Files, ClipboardCheck, Building2, type LucideIcon } from 'lucide-react';
import { GradientHero } from '@/components/ui/glass';
import ITW7Return from '@/features/burs/ITW7Return';
import ITW8Certificates from '@/features/burs/ITW8Certificates';
import ITW10Reconciliation from '@/features/burs/ITW10Reconciliation';
import EmployerDetails from '@/features/burs/EmployerDetails';

type HubTab = 'itw7' | 'itw8' | 'itw10' | 'employer';

const TABS: { id: HubTab; label: string; icon: LucideIcon }[] = [
  { id: 'itw7', label: 'ITW-7 · Monthly', icon: FileText },
  { id: 'itw8', label: 'ITW-8 · Certificates', icon: Files },
  { id: 'itw10', label: 'ITW-10 · Annual', icon: ClipboardCheck },
  { id: 'employer', label: 'Employer', icon: Building2 },
];

export default function BursHub() {
  const [tab, setTab] = useState<HubTab>('itw7');

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Landmark}
        title="BURS Returns"
        subtitle="PAYE statutory filings · ITW-7 · ITW-8 · ITW-10"
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
        {TABS.map((t) => {
          const Icon = t.icon;
          const activeTab = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                activeTab
                  ? 'border-[var(--accent-strong,#1b3a6b)] text-[var(--accent-link,#2d5295)] dark:text-[var(--accent-link-dark,#93c5fd)]'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'itw7' && <ITW7Return />}
      {tab === 'itw8' && <ITW8Certificates />}
      {tab === 'itw10' && <ITW10Reconciliation />}
      {tab === 'employer' && <EmployerDetails />}
    </div>
  );
}
